import { utils } from "@vilocnv/allsetra-core";
import { DateTime } from "luxon";
import moment from "moment";

export const formatTimeWithDay = (timeStamp: string) => {
  const time = DateTime.fromISO(timeStamp, { zone: "utc" }).toFormat(
    "MM/d/yyyy HH:mm:ss"
  );
  return time;
};

export const formatTimeStamp = (timeStamp: string) => {
  const time = DateTime.fromISO(timeStamp, { zone: "utc" }).toFormat(
    "HH:mm:ss"
  );
  return time;
};

export type GroupBy = "day" | "week" | "month" | "year";

export const getDateKey = (date: string, groupBy: GroupBy): string => {
  // Parse the date
  const parsedDate = DateTime.fromISO(date);

  if (!parsedDate.isValid) {
    return "Invalid Date";
  }

  switch (groupBy) {
    case "day":
      return utils.formatDate({
        date: parsedDate?.toJSDate(),
        dateFormat: "dd-MMM-yyyy"
      });

    case "week": {
      const startOfWeek = parsedDate.startOf("week");
      const endOfWeek = startOfWeek.endOf("week");
      return `${startOfWeek.toFormat("MMM d yyyy")} - ${endOfWeek.toFormat("MMM d yyyy")}`;
    }

    case "month":
      return parsedDate.toFormat("yyyy-MM");

    case "year":
      return parsedDate.toFormat("yyyy");

    default:
      throw new Error(`Invalid groupBy value: ${groupBy}`);
  }
};

export const convertToNormalTime = (dateString: Date, endDate?: boolean) => {
  const date = new Date(dateString);

  if (endDate) {
    date.setHours(23, 59, 59);
  } else {
    date.setHours(0, 0, 0);
  }

  const year = date.getFullYear();
  const month = date.getMonth() + 1; // Adding 1 because getMonth() returns zero-based month
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();

  const formattedTime = `${year}-${month < 10 ? "0" + month : month}-${
    day < 10 ? "0" + day : day
  }T${hours < 10 ? "0" + hours : hours}:${
    minutes < 10 ? "0" + minutes : minutes
  }:${seconds < 10 ? "0" + seconds : seconds}.00Z`;

  return formattedTime;
};

export const convertTimeToISO = (timeString: string) => {
  if (timeString === null) return null;
  if (timeString?.includes("z")) return timeString;

  const currentDate = new Date();

  const year = currentDate.getUTCFullYear();
  const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getUTCDate()).padStart(2, "0");

  const date = `${year}-${month}-${day}`;

  const dateTimeString = `${date}T${timeString}:00.000Z`;
  const dateObj = new Date(dateTimeString);

  return dateObj.toISOString();
};

export const convertISOToTime = (isoString: string | null): string | null => {
  if (!isoString || isoString.trim() === "") return null;

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeInSourceTZ = moment.parseZone(isoString); // keep original offset

  if (!timeInSourceTZ.isValid()) return null;

  const timeInUserTZ = timeInSourceTZ.tz(userTimeZone);
  const formatted = timeInUserTZ.format("HH:mm");

  return formatted && !formatted.includes("Invalid") ? formatted : null;
};

export const convertToUTC = (deliveryTime: string) => {
  if (deliveryTime === null) return null;

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Create a full datetime by adding today's date to the time string
  const today = moment().format("YYYY-MM-DD");
  const timeWithDate = `${today} ${deliveryTime}`;

  // Use moment-timezone to convert from local time to UTC
  // This properly handles DST by using the timezone rules for the specific date/time
  const timeInUserTZ = moment.tz(
    timeWithDate,
    "YYYY-MM-DD HH:mm:ss",
    userTimeZone
  );
  const timeInUTC = timeInUserTZ.utc();

  return timeInUTC.format("HH:mm:ss");
};

export const convertToLocalTime = (utcTime: string) => {
  if (utcTime === null) return "";

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Create a full datetime by adding today's date to the time string
  const today = moment.utc().format("YYYY-MM-DD");
  const timeWithDate = `${today} ${utcTime}`;

  // Use moment-timezone to convert from UTC to local time
  // This properly handles DST by using the timezone rules for the specific date/time
  const timeInUTC = moment.utc(timeWithDate, "YYYY-MM-DD HH:mm:ss");
  const timeInUserTZ = timeInUTC.tz(userTimeZone);

  return timeInUserTZ.format("HH:mm:ss");
};

export const sortDataByAlphabet = (data: any[], key: string) => {
  return data && data?.length && key
    ? data
        .filter((item: any) => item[key])
        .sort((a: any, b: any) => a[key].localeCompare(b[key]))
    : [];
};
